import React, { useEffect, useState, useMemo } from 'react'
import { Grid, Box, Button, Chip, Typography, Skeleton, Switch } from '@mui/material'
import { styled } from '@mui/material/styles'
import { useHistory } from 'react-router-dom'
import { PieChart, Pie, Cell, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, Legend } from 'recharts'
import YoutubePlayer from '../../components/Common/YoutubePlayer'
import MusicLoader from '../../components/Common/MusicLoader'
import * as actions from "./actions"
import * as userActions from "../Profile/actions"
import { toastError } from '../../utils'
import SEO from '../../components/SEO'
import whatsappIcon from '../../assets/img/whatsapp.png'

// Icons
import NewspaperIcon from '@mui/icons-material/Newspaper'
import TrendingUpIcon from '@mui/icons-material/TrendingUp'
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline'
import AlbumIcon from '@mui/icons-material/Album'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import SupportAgentIcon from '@mui/icons-material/SupportAgent'
import AddIcon from '@mui/icons-material/Add'
import LibraryMusicIcon from '@mui/icons-material/LibraryMusic'
import ConfirmationNumberIcon from '@mui/icons-material/ConfirmationNumber'
import AudiotrackIcon from '@mui/icons-material/Audiotrack'
import PersonIcon from '@mui/icons-material/Person'
import AssessmentIcon from '@mui/icons-material/Assessment'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import StarIcon from '@mui/icons-material/Star'
import WhatshotIcon from '@mui/icons-material/Whatshot'

// Styled Components
const DashboardContainer = styled(Box)({
    padding: '24px',
    minHeight: 'calc(100vh - 100px)',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #e8ecf1 100%)',
})

const MetricCard = styled(Box)(({ gradient }) => ({
    background: gradient || 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    borderRadius: '16px',
    padding: '24px',
    color: '#fff',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
    transition: 'all 0.3s ease',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        right: '-20%',
        width: '150px',
        height: '150px',
        borderRadius: '50%',
        background: 'rgba(255, 255, 255, 0.1)',
    },
    '&:hover': {
        transform: 'translateY(-8px)',
        boxShadow: '0 12px 32px rgba(0, 0, 0, 0.15)',
    },
}))

const MetricValue = styled(Typography)({
    fontSize: '2.5rem',
    fontWeight: 700,
    marginTop: '8px',
    fontFamily: "'DM Sans', sans-serif",
})

const MetricLabel = styled(Typography)({
    fontSize: '0.875rem',
    opacity: 0.9,
    textTransform: 'uppercase',
    letterSpacing: '1px',
    fontFamily: "'DM Sans', sans-serif",
})

const ChartCard = styled(Box)({
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(0, 0, 0, 0.15)',
        borderRadius: '10px',
        '&:hover': {
            background: 'rgba(0, 0, 0, 0.25)',
        }
    },
})

const ChartTitle = styled(Typography)({
    fontSize: '1.25rem',
    fontWeight: 600,
    marginBottom: '20px',
    color: '#202124',
    fontFamily: "'DM Sans', sans-serif",
})

const QuickActionCard = styled(Box)({
    background: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
})

const ActionButton = styled(Button)({
    background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
    color: '#fff',
    padding: '12px 24px',
    borderRadius: '8px',
    textTransform: 'none',
    fontSize: '1rem',
    fontWeight: 600,
    marginBottom: '12px',
    width: '100%',
    fontFamily: "'DM Sans', sans-serif",
    '&:hover': {
        background: 'linear-gradient(135deg, #1557b0 0%, #009680 100%)',
    },
})

const ReleaseCard = styled(Box)({
    background: '#f8f9fa',
    borderRadius: '10px',
    padding: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    border: '1px solid #e8ecf1',
    transition: 'all 0.3s ease',
    height: '100%',
    minHeight: '90px',
    '&:hover': {
        background: '#fff',
        transform: 'translateX(4px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        borderColor: '#1a73e8',
    },
})

const AlbumArt = styled('img')({
    width: '65px',
    height: '65px',
    borderRadius: '8px',
    objectFit: 'cover',
    flexShrink: 0,
})

const NewsCard = styled(Box)({
    background: '#f8f9fa',
    borderRadius: '12px',
    padding: '16px',
    marginBottom: '12px',
    border: '1px solid #e8ecf1',
    borderLeft: '4px solid #1a73e8',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
        background: '#fff',
        transform: 'translateX(4px)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
        borderLeftColor: '#00bfa5',
    },
    '&:last-child': {
        marginBottom: 0,
    },
})

const NewsHeadline = styled('a')({
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#202124',
    textDecoration: 'none',
    display: 'block',
    lineHeight: 1.4,
    fontFamily: "'DM Sans', sans-serif",
    '&:hover': {
        color: '#1a73e8',
    },
})

const NewsBadge = styled(Box)({
    display: 'inline-block',
    padding: '4px 12px',
    borderRadius: '20px',
    fontSize: '0.7rem',
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginTop: '8px',
})

const VideoCard = styled(Box)({
    borderRadius: '12px',
    overflow: 'hidden',
    background: '#fff',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06)',
    transition: 'all 0.3s ease',
    position: 'relative',
    paddingTop: '56.25%', // 16:9 aspect ratio
    maxHeight: '220px',
    '& iframe': {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
    },
    '&:hover': {
        transform: 'translateY(-4px)',
        boxShadow: '0 8px 20px rgba(26, 115, 232, 0.15)',
    },
})

const SectionHeader = styled(Box)({
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '20px',
    marginTop: '32px',
    '& h2': {
        margin: 0,
        fontSize: '1.5rem',
        fontWeight: 700,
        color: '#202124',
        fontFamily: "'DM Sans', sans-serif",
    },
    '& .icon': {
        fontSize: '1.75rem',
        background: 'linear-gradient(135deg, #1a73e8 0%, #00bfa5 100%)',
        WebkitBackgroundClip: 'text',
        WebkitTextFillColor: 'transparent',
        backgroundClip: 'text',
    },
})

const LoaderContainer = styled(Box)({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 'calc(100vh - 150px)',
    flexDirection: 'column',
    gap: '20px',
})

// Status badge component
const StatusBadge = ({ status }) => {
    const getStatusColor = () => {
        switch (status) {
            case 'approved': return '#4caf50'
            case 'in_review': return '#ff9800'
            case 'draft': return '#2196f3'
            case 'rejected': return '#f44336'
            case 'takendown': return '#9e9e9e'
            default: return '#757575'
        }
    }

    return (
        <Chip
            label={status.replace('_', ' ').toUpperCase()}
            size="small"
            sx={{
                backgroundColor: getStatusColor(),
                color: '#fff',
                fontWeight: 600,
                fontSize: '0.65rem',
                height: '24px',
                '& .MuiChip-label': {
                    padding: '0 8px',
                }
            }}
        />
    )
}

export default function Dashboard() {
    const history = useHistory()
    const [analytics, setAnalytics] = useState(null)
    const [videoLinks, setVideoLinks] = useState([])
    const [news, setNews] = useState([])
    const [isLoading, setIsLoading] = useState(true)
    const [topPerformers, setTopPerformers] = useState(null)
    const [topPerformersLoading, setTopPerformersLoading] = useState(true)
    const [isInr, setIsInr] = useState(false)
    const [conversionRate, setConversionRate] = useState(0)

    // Keywords for news
    const keywords = [
        { text: 'Insightful', color: '#1a73e8' },
        { text: 'Helpful', color: '#00bfa5' },
        { text: 'Important', color: '#ff6f00' },
        { text: 'Trending', color: '#f50057' },
    ]

    // Memoize keyword assignments to prevent them from changing on every render
    const newsWithKeywords = useMemo(() => {
        const getRandomItem = (arr) => arr[Math.floor(Math.random() * arr.length)]
        return news.map(item => ({
            ...item,
            keyword: getRandomItem(keywords)
        }))
    }, [news])

    useEffect(() => {
        // Load main dashboard data
        setIsLoading(true)
        Promise.all([
            actions.getDashboardAnalytics(),
            actions.getVideoLinks(),
            actions.getNews()
        ]).then(([analyticsRes, videoRes, newsRes]) => {
            setAnalytics(analyticsRes.body)
            setVideoLinks(videoRes.body)
            setNews(newsRes.body)
        }).catch(err => {
            toastError(err.message || 'Failed to load dashboard data')
        }).finally(() => {
            setIsLoading(false)
        })

        // Load top performers separately - Commented out for now
        // setTopPerformersLoading(true)
        // actions.getTopPerformers()
        //     .then((res) => {
        //         setTopPerformers(res.body.top_performers)
        //     })
        //     .catch(err => {
        //         console.error('Failed to load top performers:', err)
        //     })
        //     .finally(() => {
        //         setTopPerformersLoading(false)
        //     })

        // Fetch user data for conversion rate
        userActions.getUser().then((res) => {
            setConversionRate((res.body && res.body.conversionRate && res.body.conversionRate) || 0)
        })
    }, [])

    // Prepare chart data
    const releaseStatusData = analytics ? [
        { name: 'Approved', value: analytics.approved_releases || 0, color: '#4caf50' },
        { name: 'In Review', value: analytics.in_review_releases || 0, color: '#ff9800' },
        { name: 'Draft', value: analytics.draft_releases || 0, color: '#2196f3' },
        { name: 'Rejected', value: analytics.rejected_releases || 0, color: '#f44336' },
        { name: 'Takendown', value: analytics.takendown_releases || 0, color: '#9e9e9e' },
    ] : []

    const genreData = analytics?.top_genres?.map(g => ({
        name: g.genre,
        count: g.count
    })) || []

    const handleToggle = (event) => {
        setIsInr(event.target.checked)
    }

    const displayedRevenue = isInr
        ? (Number(analytics?.user_revenue || 0) * parseFloat(conversionRate))
        : Number(analytics?.user_revenue || 0)

    if (isLoading) {
        return <MusicLoader />
    }

    return (
        <DashboardContainer>
            <SEO
                title="Dashboard"
                description="View your music distribution analytics, recent releases, and performance metrics."
            />
            {/* Metrics Cards */}
            <Grid container spacing={3}>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard gradient="linear-gradient(135deg, #667eea 0%, #764ba2 100%)">
                        <Box display="flex" alignItems="center" gap={1}>
                            <AlbumIcon sx={{ fontSize: 32 }} />
                            <MetricLabel>Total Releases</MetricLabel>
                        </Box>
                        <MetricValue>{analytics?.total_releases || 0}</MetricValue>
                    </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard gradient="linear-gradient(135deg, #f093fb 0%, #f5576c 100%)">
                        <Box display="flex" alignItems="center" gap={1}>
                            <CheckCircleIcon sx={{ fontSize: 32 }} />
                            <MetricLabel>Approved</MetricLabel>
                        </Box>
                        <MetricValue>{analytics?.approved_releases || 0}</MetricValue>
                    </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard gradient="linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)">
                        <Box display="flex" alignItems="center" justifyContent="space-between" gap={1} mb={1}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <MonetizationOnIcon sx={{ fontSize: 32 }} />
                                <MetricLabel>Revenue</MetricLabel>
                            </Box>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                background: 'rgba(255, 255, 255, 0.2)',
                                borderRadius: '20px',
                                padding: '4px 8px',
                            }}>
                                <Typography sx={{
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    color: isInr ? 'rgba(0, 0, 0, 0.5)' : '#000',
                                    transition: 'all 0.3s ease',
                                }}>
                                    $
                                </Typography>
                                <Switch
                                    checked={isInr}
                                    onChange={handleToggle}
                                    sx={{
                                        width: 42,
                                        height: 22,
                                        padding: 0,
                                        '& .MuiSwitch-switchBase': {
                                            padding: 0,
                                            margin: '2px',
                                            transitionDuration: '300ms',
                                            '&.Mui-checked': {
                                                transform: 'translateX(20px)',
                                                color: '#fff',
                                                '& + .MuiSwitch-track': {
                                                    backgroundColor: '#00bfa5',
                                                    opacity: 1,
                                                    border: 0,
                                                },
                                            },
                                        },
                                        '& .MuiSwitch-thumb': {
                                            boxSizing: 'border-box',
                                            width: 18,
                                            height: 18,
                                            backgroundColor: '#fff',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                                        },
                                        '& .MuiSwitch-track': {
                                            borderRadius: 11,
                                            backgroundColor: '#1a73e8',
                                            opacity: 1,
                                            transition: 'background-color 300ms',
                                        },
                                    }}
                                />
                                <Typography sx={{
                                    fontSize: '1.1rem',
                                    fontWeight: 700,
                                    color: isInr ? '#000' : 'rgba(0, 0, 0, 0.5)',
                                    transition: 'all 0.3s ease',
                                }}>
                                    ₹
                                </Typography>
                            </Box>
                        </Box>
                        <MetricValue>{isInr ? "₹" : "$"}{displayedRevenue.toFixed(2)}</MetricValue>
                    </MetricCard>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                    <MetricCard gradient="linear-gradient(135deg, #fa709a 0%, #fee140 100%)">
                        <Box display="flex" alignItems="center" gap={1}>
                            <SupportAgentIcon sx={{ fontSize: 32 }} />
                            <MetricLabel>Open Tickets</MetricLabel>
                        </Box>
                        <MetricValue>{analytics?.open_tickets || 0}</MetricValue>
                    </MetricCard>
                </Grid>
            </Grid>

            {/* Charts Row */}
            <Grid container spacing={3} sx={{ marginTop: 2, alignItems: 'stretch' }}>
                {/* Release Status Chart */}
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                    <ChartCard sx={{
                        background: 'linear-gradient(135deg, #f093fb35 0%, #f5576c30 50%, #fa709a25 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        width: '100%',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '-20%',
                            right: '-5%',
                            width: '300px',
                            height: '300px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #f093fb40 0%, transparent 70%)',
                            zIndex: 0,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: '-30%',
                            left: '-5%',
                            width: '250px',
                            height: '250px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #fee14035 0%, transparent 70%)',
                            zIndex: 0,
                        }
                    }}>
                        <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Release Status Distribution</ChartTitle>
                        <Box sx={{
                            display: 'flex',
                            flexDirection: { xs: 'column', sm: 'column', md: 'column', lg: 'row' },
                            alignItems: { xs: 'center', lg: 'center' },
                            justifyContent: { xs: 'center', lg: 'space-between' },
                            gap: { xs: 2, lg: 3 },
                            position: 'relative',
                            zIndex: 1,
                            paddingTop: 1,
                            paddingBottom: 0
                        }}>
                            {/* Circular Chart */}
                            <Box sx={{
                                flex: { xs: 'none', lg: 1 },
                                width: { xs: '100%', sm: '280px', lg: 'auto' },
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}>
                                <ResponsiveContainer width="100%" height={240}>
                                    <PieChart>
                                        <Pie
                                            data={releaseStatusData}
                                            cx="50%"
                                            cy="50%"
                                            innerRadius={55}
                                            outerRadius={95}
                                            paddingAngle={4}
                                            dataKey="value"
                                            stroke="#fff"
                                            strokeWidth={1.5}
                                            cornerRadius={8}
                                            strokeLinejoin="round"
                                        >
                                            {releaseStatusData.map((entry, index) => (
                                                <Cell key={`cell-${index}`} fill={entry.color} />
                                            ))}
                                        </Pie>
                                        <Tooltip
                                            contentStyle={{
                                                backgroundColor: '#202124',
                                                border: 'none',
                                                borderRadius: '8px',
                                                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                                                color: '#fff',
                                                padding: '12px'
                                            }}
                                            itemStyle={{ color: '#fff', fontSize: '0.85rem', fontWeight: 600 }}
                                        />
                                    </PieChart>
                                </ResponsiveContainer>
                            </Box>

                            {/* Custom Legend - Right Side 2 Columns */}
                            <Box sx={{
                                display: 'flex',
                                gap: 2,
                                paddingRight: { xs: 0, lg: 2 },
                                width: { xs: '100%', lg: 'auto' },
                                justifyContent: 'center',
                            }}>
                                {/* First Column - 3 items */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {releaseStatusData.slice(0, 3).map((status, index) => {
                                        const totalReleases = releaseStatusData.reduce((sum, s) => sum + s.value, 0);
                                        const percentage = totalReleases > 0 ? Math.round((status.value / totalReleases) * 100) : 0;

                                        return (
                                            <Box
                                                key={index}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    padding: { xs: '10px 12px', lg: '12px 14px' },
                                                    minWidth: { xs: '140px', sm: '150px' },
                                                    background: '#fff',
                                                    borderRadius: '12px',
                                                    border: `2px solid ${status.color}`,
                                                    boxShadow: `0 2px 8px ${status.color}20`,
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: `0 4px 12px ${status.color}40`,
                                                    }
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        background: status.color,
                                                        flexShrink: 0,
                                                        boxShadow: `0 0 10px ${status.color}70`
                                                    }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0.5 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                color: '#202124',
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {status.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                color: '#5f6368',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            ({percentage}%)
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontSize: '1.4rem',
                                                            fontWeight: 700,
                                                            color: status.color,
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        {status.value}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>

                                {/* Second Column - 2 items */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                    {releaseStatusData.slice(3, 5).map((status, index) => {
                                        const totalReleases = releaseStatusData.reduce((sum, s) => sum + s.value, 0);
                                        const percentage = totalReleases > 0 ? Math.round((status.value / totalReleases) * 100) : 0;

                                        return (
                                            <Box
                                                key={index + 3}
                                                sx={{
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1.5,
                                                    padding: { xs: '10px 12px', lg: '12px 14px' },
                                                    minWidth: { xs: '140px', sm: '150px' },
                                                    background: '#fff',
                                                    borderRadius: '12px',
                                                    border: `2px solid ${status.color}`,
                                                    boxShadow: `0 2px 8px ${status.color}20`,
                                                    transition: 'all 0.3s ease',
                                                    cursor: 'pointer',
                                                    '&:hover': {
                                                        transform: 'translateY(-2px)',
                                                        boxShadow: `0 4px 12px ${status.color}40`,
                                                    }
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: '12px',
                                                        height: '12px',
                                                        borderRadius: '50%',
                                                        background: status.color,
                                                        flexShrink: 0,
                                                        boxShadow: `0 0 10px ${status.color}70`
                                                    }}
                                                />
                                                <Box sx={{ flex: 1 }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 0.5 }}>
                                                        <Typography
                                                            variant="body2"
                                                            sx={{
                                                                fontSize: '0.75rem',
                                                                fontWeight: 600,
                                                                color: '#202124',
                                                                lineHeight: 1.2,
                                                            }}
                                                        >
                                                            {status.name}
                                                        </Typography>
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontSize: '0.65rem',
                                                                color: '#5f6368',
                                                                fontWeight: 600
                                                            }}
                                                        >
                                                            ({percentage}%)
                                                        </Typography>
                                                    </Box>
                                                    <Typography
                                                        variant="h6"
                                                        sx={{
                                                            fontSize: '1.4rem',
                                                            fontWeight: 700,
                                                            color: status.color,
                                                            lineHeight: 1
                                                        }}
                                                    >
                                                        {status.value}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>
                        </Box>
                    </ChartCard>
                </Grid>

                {/* Genre Distribution Chart */}
                <Grid item xs={12} md={6} sx={{ display: 'flex' }}>
                    <ChartCard sx={{
                        width: '100%',
                        background: 'linear-gradient(135deg, #4facfe20 0%, #00f2fe25 50%, #43e97b15 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '-20%',
                            left: '-5%',
                            width: '280px',
                            height: '280px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #4facfe25 0%, transparent 70%)',
                            zIndex: 0,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: '-25%',
                            right: '-5%',
                            width: '260px',
                            height: '260px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #43e97b20 0%, transparent 70%)',
                            zIndex: 0,
                        }
                    }}>
                        <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Your Top Genres</ChartTitle>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'center', position: 'relative', zIndex: 1, paddingTop: 1, paddingBottom: 0 }}>
                            {genreData.length > 0 ? (
                                <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    {genreData.map((genre, index) => {
                                        const colors = [
                                            { bg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', text: '#667eea' },
                                            { bg: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', text: '#f5576c' },
                                            { bg: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)', text: '#00f2fe' },
                                            { bg: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)', text: '#38f9d7' },
                                            { bg: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)', text: '#fa709a' },
                                        ];
                                        const maxCount = Math.max(...genreData.map(g => g.count));
                                        const percentage = (genre.count / maxCount) * 100;
                                        const color = colors[index % colors.length];

                                        return (
                                            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
                                                <Typography
                                                    variant="body2"
                                                    sx={{
                                                        width: '140px',
                                                        fontWeight: 600,
                                                        color: '#202124',
                                                        fontSize: '0.85rem',
                                                        flexShrink: 0
                                                    }}
                                                >
                                                    {genre.name}
                                                </Typography>
                                                <Box sx={{ flex: 1, position: 'relative', height: '32px' }}>
                                                    <Box
                                                        sx={{
                                                            position: 'absolute',
                                                            left: 0,
                                                            top: 0,
                                                            height: '100%',
                                                            width: `${percentage}%`,
                                                            background: color.bg,
                                                            borderRadius: '8px',
                                                            transition: 'width 0.8s ease',
                                                            boxShadow: `0 2px 8px ${color.text}30`,
                                                        }}
                                                    />
                                                    <Typography
                                                        variant="caption"
                                                        sx={{
                                                            position: 'absolute',
                                                            right: 8,
                                                            top: '50%',
                                                            transform: 'translateY(-50%)',
                                                            fontWeight: 700,
                                                            color: '#202124',
                                                            zIndex: 1,
                                                            fontSize: '0.75rem'
                                                        }}
                                                    >
                                                        {genre.count}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            ) : (
                                <Typography sx={{ color: '#5f6368', textAlign: 'center' }}>
                                    No genre data available yet
                                </Typography>
                            )}
                        </Box>
                    </ChartCard>
                </Grid>
            </Grid>

            {/* Quick Stats & Recent Releases */}
            <Grid container spacing={3} sx={{ marginTop: 2 }}>
                {/* Quick Stats */}
                <Grid item xs={12} md={4}>
                    <QuickActionCard sx={{
                        minHeight: '420px',
                        background: 'linear-gradient(135deg, #667eea18 0%, #764ba220 50%, #f093fb18 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '-15%',
                            right: '-10%',
                            width: '200px',
                            height: '200px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #667eea20 0%, transparent 70%)',
                            zIndex: 0,
                        }
                    }}>
                        <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Quick Stats</ChartTitle>
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, flex: 1, position: 'relative', zIndex: 1 }}>
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                padding: '20px 18px',
                                minHeight: '85px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '2px solid #667eea30',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 8px rgba(102, 126, 234, 0.1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(102, 126, 234, 0.25)',
                                    borderColor: '#667eea'
                                }
                            }}>
                                <AudiotrackIcon sx={{ fontSize: 40, color: '#667eea' }} />
                                <Box flex={1}>
                                    <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>
                                        Total Tracks
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: '#202124', fontWeight: 700, fontSize: '1.65rem', lineHeight: 1.1 }}>
                                        {analytics?.total_tracks || 0}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                padding: '20px 18px',
                                minHeight: '85px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '2px solid #f093fb30',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 8px rgba(240, 147, 251, 0.1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(240, 147, 251, 0.25)',
                                    borderColor: '#f093fb'
                                }
                            }}>
                                <PersonIcon sx={{ fontSize: 40, color: '#f5576c' }} />
                                <Box flex={1}>
                                    <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>
                                        Total Artists
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: '#202124', fontWeight: 700, fontSize: '1.65rem', lineHeight: 1.1 }}>
                                        {analytics?.total_artists || 0}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                padding: '20px 18px',
                                minHeight: '85px',
                                background: '#fff',
                                borderRadius: '12px',
                                border: '2px solid #4facfe30',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 2px 8px rgba(79, 172, 254, 0.1)',
                                '&:hover': {
                                    transform: 'translateY(-2px)',
                                    boxShadow: '0 6px 16px rgba(79, 172, 254, 0.25)',
                                    borderColor: '#4facfe'
                                }
                            }}>
                                <AssessmentIcon sx={{ fontSize: 40, color: '#00f2fe' }} />
                                <Box flex={1}>
                                    <Typography variant="caption" sx={{ color: '#5f6368', display: 'block', fontSize: '0.8rem', fontWeight: 600, marginBottom: '4px' }}>
                                        Pending Reports
                                    </Typography>
                                    <Typography variant="h5" sx={{ color: '#202124', fontWeight: 700, fontSize: '1.65rem', lineHeight: 1.1 }}>
                                        {analytics?.pending_reports || 0}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </QuickActionCard>
                </Grid>

                {/* Recent Releases */}
                <Grid item xs={12} md={8}>
                    <QuickActionCard sx={{
                        minHeight: '420px',
                        background: 'linear-gradient(135deg, #43e97b18 0%, #38f9d720 50%, #4facfe18 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: '-15%',
                            left: '-5%',
                            width: '250px',
                            height: '250px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #43e97b20 0%, transparent 70%)',
                            zIndex: 0,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: '-10%',
                            right: '-5%',
                            width: '220px',
                            height: '220px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #4facfe20 0%, transparent 70%)',
                            zIndex: 0,
                        }
                    }}>
                        <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Your Recent Releases</ChartTitle>
                        <Box sx={{ flex: 1, position: 'relative', zIndex: 1 }}>
                            {analytics?.recent_releases && analytics.recent_releases.length > 0 ? (
                                <Grid container spacing={2}>
                                    {analytics.recent_releases.map((release, index) => (
                                        <Grid item xs={12} sm={6} key={index}>
                                            <ReleaseCard>
                                                <AlbumArt
                                                    src={release.album_artwork}
                                                    alt={release.release_title}
                                                    onError={(e) => {
                                                        // Remove error handler to prevent infinite loop
                                                        e.target.onerror = null;
                                                        // Use a data URI as fallback (no server request needed)
                                                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwIiBoZWlnaHQ9IjEwMCIgZmlsbD0iI2UwZTBlMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM5OTk5OTkiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=';
                                                    }}
                                                />
                                                <Box flex={1} sx={{ minWidth: 0 }}>
                                                    <Typography variant="subtitle2" fontWeight={600} sx={{
                                                        color: '#202124',
                                                        fontSize: '0.95rem',
                                                        marginBottom: '4px',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap'
                                                    }}>
                                                        {release.release_title}
                                                    </Typography>
                                                    <Typography variant="caption" sx={{
                                                        color: '#5f6368',
                                                        fontSize: '0.8rem',
                                                        overflow: 'hidden',
                                                        textOverflow: 'ellipsis',
                                                        whiteSpace: 'nowrap',
                                                        display: 'block'
                                                    }}>
                                                        {release.artist_name}
                                                    </Typography>
                                                </Box>
                                                <StatusBadge status={release.status} />
                                            </ReleaseCard>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : (
                                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
                                    <Typography sx={{ textAlign: 'center', color: '#5f6368' }}>
                                        No recent releases found. Start by creating your first release!
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </QuickActionCard>
                </Grid>
            </Grid>

            {/* Top Performers of All Time - Commented out for now */}
            {/* <Grid container spacing={3} sx={{ marginTop: 2 }}>
                <Grid item xs={12}>
                    <ChartCard sx={{
                        background: 'linear-gradient(135deg, #fad0c4 0%, #ffd1ff 50%, #a8edea 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '-20%',
                            right: '-5%',
                            width: '280px',
                            height: '280px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #fad0c430 0%, transparent 70%)',
                            zIndex: 0,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            bottom: '-20%',
                            left: '-5%',
                            width: '250px',
                            height: '250px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #a8edea30 0%, transparent 70%)',
                            zIndex: 0,
                        }
                    }}>
                        <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Top Performers of All Time</ChartTitle>
                        <Box sx={{ position: 'relative', zIndex: 1, paddingTop: 1 }}>
                            {topPerformersLoading ? (
                                <Grid container spacing={2}>
                                    {[1, 2, 3, 4, 5].map((item) => (
                                        <Grid item xs={12} sm={6} md={2.4} key={item}>
                                            <Box sx={{
                                                background: 'linear-gradient(135deg, #ffffff 0%, #fafafa 100%)',
                                                borderRadius: '16px',
                                                padding: '20px 14px 16px',
                                                textAlign: 'center',
                                                border: '3px solid #e8ecf1',
                                                height: '360px',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                alignItems: 'center',
                                                gap: 1.5,
                                                position: 'relative',
                                                overflow: 'hidden',
                                            }}>
                                                {/* Top Ribbon Skeleton */}
            {/*             <Skeleton variant="text" width="60%" height={20} sx={{ marginTop: '8px', bgcolor: '#d0d0d0' }} />

                                                {/* Trophy Circle Skeleton */}
            {/*             <Skeleton variant="circular" width={70} height={70} sx={{ marginTop: '20px', bgcolor: '#d0d0d0' }} />

                                                {/* Name Skeleton */}
            {/*             <Skeleton variant="text" width="70%" height={16} sx={{ marginTop: '8px', bgcolor: '#d0d0d0' }} />

                                                {/* Stats Boxes Skeleton */}
            {/*             <Box sx={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 1, marginTop: '8px' }}>
                                                    <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: '10px', bgcolor: '#d0d0d0' }} />
                                                    <Skeleton variant="rectangular" width="100%" height={60} sx={{ borderRadius: '10px', bgcolor: '#d0d0d0' }} />
                                                </Box>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>
                            ) : topPerformers && topPerformers.length > 0 ? (
                                <Grid container spacing={4}>
                                    {topPerformers.map((performer, index) => {
                                        const rankerStyles = [
                                            {
                                                color: '#FFD700',
                                                gradientBg: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                                lightBg: 'linear-gradient(135deg, #FFD70015 0%, #FFA50015 100%)',
                                                border: '#FFD70050',
                                                shadowColor: '#FFD700',
                                                ribbonBg: 'linear-gradient(135deg, #FFD700 0%, #FF8C00 100%)',
                                                title: '1ST PLACE',
                                                icon: '🏆'
                                            },
                                            {
                                                color: '#00D4FF',
                                                gradientBg: 'linear-gradient(135deg, #00D4FF 0%, #0099CC 100%)',
                                                lightBg: 'linear-gradient(135deg, #00D4FF15 0%, #0099CC15 100%)',
                                                border: '#00D4FF50',
                                                shadowColor: '#00D4FF',
                                                ribbonBg: 'linear-gradient(135deg, #00D4FF 0%, #0088BB 100%)',
                                                title: '2ND PLACE',
                                                icon: '🏆'
                                            },
                                            {
                                                color: '#FF6B9D',
                                                gradientBg: 'linear-gradient(135deg, #FF6B9D 0%, #C44569 100%)',
                                                lightBg: 'linear-gradient(135deg, #FF6B9D15 0%, #C4456915 100%)',
                                                border: '#FF6B9D50',
                                                shadowColor: '#FF6B9D',
                                                ribbonBg: 'linear-gradient(135deg, #FF6B9D 0%, #C44569 100%)',
                                                title: '3RD PLACE',
                                                icon: '🏆'
                                            },
                                            {
                                                color: '#667eea',
                                                gradientBg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                lightBg: 'linear-gradient(135deg, #667eea15 0%, #764ba215 100%)',
                                                border: '#667eea40',
                                                shadowColor: '#667eea',
                                                ribbonBg: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                title: '4TH PLACE',
                                                icon: '🏆'
                                            },
                                            {
                                                color: '#7FD957',
                                                gradientBg: 'linear-gradient(135deg, #7FD957 0%, #5FA83C 100%)',
                                                lightBg: 'linear-gradient(135deg, #7FD95715 0%, #5FA83C15 100%)',
                                                border: '#7FD95740',
                                                shadowColor: '#7FD957',
                                                ribbonBg: 'linear-gradient(135deg, #7FD957 0%, #5FA83C 100%)',
                                                title: '5TH PLACE',
                                                icon: '🏆'
                                            }
                                        ];
                                        const style = rankerStyles[index];

                                        return (
                                            <Grid item xs={12} sm={6} md={2.4} key={index}>
                                                <Box sx={{
                                                    position: 'relative',
                                                    background: `linear-gradient(135deg, #ffffff 0%, #fafafa 100%)`,
                                                    borderRadius: '16px',
                                                    padding: '20px 14px 16px',
                                                    textAlign: 'center',
                                                    border: `3px solid ${style.border}`,
                                                    maxWidth: '700px',
                                                    boxShadow: `0 8px 24px ${style.shadowColor}30, 0 0 0 1px ${style.shadowColor}10`,
                                                    transition: 'all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275)',
                                                    height: '360px',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'space-between',
                                                    overflow: 'hidden',
                                                    '&::before': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        height: '6px',
                                                        background: style.gradientBg,
                                                        zIndex: 1,
                                                    },
                                                    '&::after': {
                                                        content: '""',
                                                        position: 'absolute',
                                                        top: '-50%',
                                                        right: '-30%',
                                                        width: '150px',
                                                        height: '150px',
                                                        borderRadius: '50%',
                                                        background: style.lightBg,
                                                        opacity: 0.4,
                                                        zIndex: 0,
                                                    },
                                                    '&:hover': {
                                                        transform: 'translateY(-8px) scale(1.02)',
                                                        boxShadow: `0 16px 40px ${style.shadowColor}40, 0 0 0 2px ${style.shadowColor}30`,
                                                        borderColor: style.color,
                                                    }
                                                }}>
                                                    {/* Decorative Stars */}
            {/*                     <Box sx={{
                                                        position: 'absolute',
                                                        top: '10px',
                                                        left: '10px',
                                                        fontSize: '1.2rem',
                                                        opacity: 0.3,
                                                        zIndex: 0,
                                                    }}>
                                                        ✨
                                                    </Box>
                                                    <Box sx={{
                                                        position: 'absolute',
                                                        bottom: '10px',
                                                        right: '10px',
                                                        fontSize: '1.2rem',
                                                        opacity: 0.3,
                                                        zIndex: 0,
                                                    }}>
                                                        ✨
                                                    </Box>

                                                    {/* Rank Badge Ribbon - Moved below stars */}
            {/*                     <Box sx={{
                                                        position: 'absolute',
                                                        top: '8px',
                                                        left: '50%',
                                                        transform: 'translateX(-50%)',
                                                        background: style.ribbonBg,
                                                        color: '#fff',
                                                        padding: '6px 16px',
                                                        fontSize: '0.65rem',
                                                        fontWeight: 800,
                                                        letterSpacing: '0.8px',
                                                        zIndex: 3,
                                                        boxShadow: `0 3px 12px ${style.shadowColor}50`,
                                                        borderRadius: '4px',
                                                        border: '2px solid rgba(255, 255, 255, 0.3)',
                                                    }}>
                                                        {style.title}
                                                    </Box>

                                                    {/* Trophy Icon with Circular Badge */}
            {/*                     <Box sx={{
                                                        position: 'relative',
                                                        zIndex: 1,
                                                        marginBottom: '4px',
                                                        marginTop: '28px',
                                                    }}>
                                                        <Box sx={{
                                                            width: '70px',
                                                            height: '70px',
                                                            borderRadius: '50%',
                                                            background: style.lightBg,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            margin: '0 auto',
                                                            border: `3px solid ${style.color}`,
                                                            boxShadow: `0 4px 16px ${style.shadowColor}40, inset 0 2px 8px ${style.shadowColor}20`,
                                                            position: 'relative',
                                                            '&::before': {
                                                                content: '""',
                                                                position: 'absolute',
                                                                top: '50%',
                                                                left: '50%',
                                                                transform: 'translate(-50%, -50%)',
                                                                width: '90%',
                                                                height: '90%',
                                                                borderRadius: '50%',
                                                                background: `radial-gradient(circle, ${style.color}15 0%, transparent 70%)`,
                                                                animation: 'pulse 2s ease-in-out infinite',
                                                                '@keyframes pulse': {
                                                                    '0%, 100%': { opacity: 1, transform: 'translate(-50%, -50%) scale(1)' },
                                                                    '50%': { opacity: 0.7, transform: 'translate(-50%, -50%) scale(1.1)' }
                                                                }
                                                            }
                                                        }}>
                                                            <Box sx={{
                                                                fontSize: '2.2rem',
                                                                position: 'relative',
                                                                zIndex: 1,
                                                                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))',
                                                            }}>
                                                                {style.icon}
                                                            </Box>
                                                        </Box>
                                                    </Box>

                                                    {/* Performer Name */}
            {/*                     <Box sx={{
                                                        position: 'relative',
                                                        zIndex: 1,
                                                        padding: '2px 4px',
                                                        marginTop: '2px',
                                                    }}>
                                                        <Typography variant="h6" sx={{
                                                            fontSize: '0.95rem',
                                                            fontWeight: 800,
                                                            color: '#202124',
                                                            overflow: 'hidden',
                                                            textOverflow: 'ellipsis',
                                                            whiteSpace: 'nowrap',
                                                            textShadow: '0 1px 2px rgba(0,0,0,0.05)',
                                                            letterSpacing: '0.3px',
                                                        }}>
                                                            {performer.name}
                                                        </Typography>
                                                    </Box>

                                                    {/* Stats Section */}
            {/*                     <Box sx={{
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 1,
                                                        position: 'relative',
                                                        zIndex: 1,
                                                        marginTop: '2px',
                                                    }}>
                                                        {/* Releases Stat */}
            {/*                         <Box sx={{
                                                            background: `linear-gradient(135deg, #fff 0%, ${style.shadowColor}08 100%)`,
                                                            borderRadius: '10px',
                                                            padding: '10px',
                                                            border: `2px solid ${style.border}`,
                                                            boxShadow: `0 2px 8px ${style.shadowColor}15`,
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                transform: 'scale(1.03)',
                                                                boxShadow: `0 4px 12px ${style.shadowColor}25`,
                                                            }
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8, marginBottom: '4px' }}>
                                                                <AlbumIcon sx={{ fontSize: 14, color: style.color }} />
                                                                <Typography variant="caption" sx={{
                                                                    fontSize: '0.65rem',
                                                                    color: '#5f6368',
                                                                    fontWeight: 700,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.5px',
                                                                }}>
                                                                    Releases
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="h6" sx={{
                                                                fontSize: '1.5rem',
                                                                fontWeight: 800,
                                                                background: style.gradientBg,
                                                                WebkitBackgroundClip: 'text',
                                                                WebkitTextFillColor: 'transparent',
                                                                backgroundClip: 'text',
                                                                lineHeight: 1,
                                                            }}>
                                                                {performer.approved_releases}
                                                            </Typography>
                                                        </Box>

                                                        {/* Tracks Stat */}
            {/*                         <Box sx={{
                                                            background: `linear-gradient(135deg, #fff 0%, ${style.shadowColor}08 100%)`,
                                                            borderRadius: '10px',
                                                            padding: '10px',
                                                            border: `2px solid ${style.border}`,
                                                            boxShadow: `0 2px 8px ${style.shadowColor}15`,
                                                            transition: 'all 0.3s ease',
                                                            '&:hover': {
                                                                transform: 'scale(1.03)',
                                                                boxShadow: `0 4px 12px ${style.shadowColor}25`,
                                                            }
                                                        }}>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.8, marginBottom: '4px' }}>
                                                                <AudiotrackIcon sx={{ fontSize: 14, color: style.color }} />
                                                                <Typography variant="caption" sx={{
                                                                    fontSize: '0.65rem',
                                                                    color: '#5f6368',
                                                                    fontWeight: 700,
                                                                    textTransform: 'uppercase',
                                                                    letterSpacing: '0.5px',
                                                                }}>
                                                                    Tracks
                                                                </Typography>
                                                            </Box>
                                                            <Typography variant="h6" sx={{
                                                                fontSize: '1.5rem',
                                                                fontWeight: 800,
                                                                background: style.gradientBg,
                                                                WebkitBackgroundClip: 'text',
                                                                WebkitTextFillColor: 'transparent',
                                                                backgroundClip: 'text',
                                                                lineHeight: 1,
                                                            }}>
                                                                {performer.audio_count}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                </Box>
                                            </Grid>
                                        );
                                    })}
                                </Grid>
                            ) : (
                                <Box sx={{ textAlign: 'center', padding: '40px 0' }}>
                                    <EmojiEventsIcon sx={{ fontSize: 60, color: '#e0e0e0', marginBottom: 2 }} />
                                    <Typography sx={{ color: '#5f6368' }}>
                                        No performer data available yet
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </ChartCard>
                </Grid>
            </Grid> */}

            {/* News and Videos Section */}
            <Grid container spacing={3} sx={{ marginTop: 2 }}>
                {/* News Section */}
                <Grid item xs={12} md={4}>
                    <ChartCard sx={{
                        minHeight: '600px',
                        maxHeight: '600px',
                        background: 'linear-gradient(135deg, #fa709a18 0%, #fee14020 50%, #ffa50018 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            top: '-10%',
                            right: '-5%',
                            width: '180px',
                            height: '180px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #fa709a25 0%, transparent 70%)',
                            zIndex: 0,
                        }
                    }}>
                        <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Latest News</ChartTitle>
                        <Box sx={{
                            position: 'relative',
                            zIndex: 1,
                            flex: 1,
                            overflowY: 'auto',
                            paddingRight: '4px',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(0, 0, 0, 0.15)',
                                borderRadius: '10px',
                                '&:hover': {
                                    background: 'rgba(0, 0, 0, 0.25)',
                                }
                            },
                        }}>
                            {newsWithKeywords.map((item, key) => {
                                return (
                                    <NewsCard key={key}>
                                        <NewsHeadline
                                            href={item.news_link}
                                            target="_blank"
                                            rel="noreferrer"
                                        >
                                            {item.news_heading}
                                        </NewsHeadline>
                                        <NewsBadge
                                            sx={{
                                                backgroundColor: `${item.keyword.color}15`,
                                                color: item.keyword.color,
                                            }}
                                        >
                                            {item.keyword.text}
                                        </NewsBadge>
                                    </NewsCard>
                                )
                            })}
                        </Box>
                    </ChartCard>
                </Grid>

                {/* Videos Section */}
                <Grid item xs={12} md={8}>
                    <ChartCard sx={{
                        minHeight: '600px',
                        maxHeight: '600px',
                        background: 'linear-gradient(135deg, #4facfe30 0%, #00f2fe35 50%, #43e97b25 100%)',
                        position: 'relative',
                        overflow: 'hidden',
                        '&::before': {
                            content: '""',
                            position: 'absolute',
                            bottom: '-10%',
                            left: '-5%',
                            width: '250px',
                            height: '250px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #4facfe35 0%, transparent 70%)',
                            zIndex: 0,
                        },
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: '-10%',
                            right: '-5%',
                            width: '200px',
                            height: '200px',
                            borderRadius: '50%',
                            background: 'radial-gradient(circle, #43e97b30 0%, transparent 70%)',
                            zIndex: 0,
                        }
                    }}>
                        <ChartTitle sx={{ position: 'relative', zIndex: 1 }}>Tutorials and Updates</ChartTitle>
                        <Box sx={{
                            position: 'relative',
                            zIndex: 1,
                            flex: 1,
                            overflowY: 'auto',
                            paddingRight: '4px',
                            '&::-webkit-scrollbar': {
                                width: '6px',
                            },
                            '&::-webkit-scrollbar-track': {
                                background: 'transparent',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'rgba(0, 0, 0, 0.15)',
                                borderRadius: '10px',
                                '&:hover': {
                                    background: 'rgba(0, 0, 0, 0.25)',
                                }
                            },
                        }}>
                            <Grid container spacing={2.5}>
                                {videoLinks.map((item, key) => {
                                    return (
                                        <Grid
                                            key={key}
                                            item
                                            xs={12}
                                            sm={12}
                                            md={6}
                                            lg={4}
                                            xl={4}
                                        >
                                            <VideoCard sx={{ width: '100%' }}>
                                                <YoutubePlayer videoId={item.video_link} />
                                            </VideoCard>
                                        </Grid>
                                    );
                                })}
                            </Grid>

                            {/* WhatsApp Follow Section */}
                            <Box sx={{
                                marginTop: '24px',
                                background: 'linear-gradient(135deg, #25D36620 0%, #128C7E20 100%)',
                                borderRadius: '16px',
                                padding: '24px',
                                border: '2px solid #25D36640',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                gap: '16px',
                                boxShadow: '0 4px 16px rgba(37, 211, 102, 0.15)',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    boxShadow: '0 6px 20px rgba(37, 211, 102, 0.25)',
                                    transform: 'translateY(-2px)',
                                }
                            }}>
                                <Box sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                }}>
                                    <Box
                                        component="img"
                                        src={whatsappIcon}
                                        alt="WhatsApp"
                                        sx={{
                                            width: '32px',
                                            height: '32px',
                                            filter: 'drop-shadow(0 2px 8px rgba(37, 211, 102, 0.3))',
                                        }}
                                    />
                                    <Typography sx={{
                                        fontSize: '1.1rem',
                                        fontWeight: 700,
                                        color: '#202124',
                                        fontFamily: "'DM Sans', sans-serif",
                                    }}>
                                        Stay Connected!
                                    </Typography>
                                </Box>

                                <Typography sx={{
                                    fontSize: '0.95rem',
                                    color: '#5f6368',
                                    textAlign: 'center',
                                    lineHeight: 1.6,
                                    fontFamily: "'DM Sans', sans-serif",
                                    maxWidth: '500px',
                                }}>
                                    For latest song releases, new updates and much more, follow us on WhatsApp.
                                </Typography>

                                <Button
                                    component="a"
                                    href="https://www.whatsapp.com/channel/0029Vako0eHJ93wUti8uWV1S"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{
                                        background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                                        color: '#fff',
                                        padding: '12px 32px',
                                        borderRadius: '10px',
                                        textTransform: 'none',
                                        fontSize: '1rem',
                                        fontWeight: 600,
                                        fontFamily: "'DM Sans', sans-serif",
                                        boxShadow: '0 4px 12px rgba(37, 211, 102, 0.3)',
                                        transition: 'all 0.3s ease',
                                        '&:hover': {
                                            background: 'linear-gradient(135deg, #128C7E 0%, #075E54 100%)',
                                            transform: 'translateY(-2px)',
                                            boxShadow: '0 6px 16px rgba(37, 211, 102, 0.4)',
                                        },
                                    }}
                                >
                                    Follow us on WhatsApp
                                </Button>
                            </Box>
                        </Box>
                    </ChartCard>
                </Grid>
            </Grid>
        </DashboardContainer>
    )
}
